<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
        </b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="activeAccountTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="accountNumber"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <template #cell(billingDate)="data">
        {{ data.value ? toDateWithYear(data.value) : '' }}
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="fetchPaymentHistories"
      @cancel="resetFilters"
    >
      <b-form>
        <b-row class="match-height">
          <!-- Reference Number -->
          <b-col md="6">
            <InputField
              id="reference-number"
              v-model="filter.referenceNumber"
              :label="$t('Reference Number')"
              :placeholder="$t('Enter Reference Number')"
              type="text"
              for="reference-number"
            />
          </b-col>
          <!-- Reference Number -->
        </b-row>
        <b-row class="match-height">
          <!-- Start Date -->
          <b-col md="6">
            <DatePickerField
              id="start-date"
              v-model="filter.startDate"
              :min-date="new Date()"
              :label="$t('Start Date')"
              :config="datePickerConfig"
              :placeholder="$t('Start Date')"
            />
          </b-col>
          <!-- Start Date -->
          <!-- End Date -->
          <b-col md="6">
            <DatePickerField
              id="end-date"
              v-model="filter.endDate"
              :min-date="new Date()"
              :label="$t('End Date')"
              :config="datePickerConfig"
              :placeholder="$t('End Date')"
            />
          </b-col>
          <!-- End Date -->
        </b-row>
      </b-form>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BForm,
} from 'bootstrap-vue'
import { required } from '@validations'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import { toYearWithDate, toDateWithYear } from '@/helpers/formatDate'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import InputField from '../../../forms/InputField.vue'
import Button from '../../../components/button/Button.vue'
import { adminPermissions } from '@/helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    InputField,
    BModal,
    BForm,
    TableEntries,
    DatePickerField,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      isReadonly: false,
      isBusy: false,
      toDateWithYear,
      required,
      modalTitle: this.$t('Subscription Invoice Report'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'referenceNumber', sortable: true, label: 'Reference Number' },
        { key: 'billableAccountsCount', sortable: true, label: 'Number of Accounts Count' },
        { key: 'transactionsCount', sortable: true, label: 'Transactions Count' },
        { key: 'billingDate', sortable: true, label: 'Billing Date' },
      ],
      filter: {
        referenceNumber: '',
        startDate: null,
        endDate: null,
      },
      datePickerConfig: {
        dateFormat: 'd/m/Y',
        maxDate: '',
        defaultDate: '',
      },
      reportData: [],
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchPaymentHistories()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchPaymentHistories()
    },
  },
  mounted() {
    this.fetchPaymentHistories()
  },
  methods: {
    ...mapActions({
      getSubscriptionInvoice: 'admin/getSubscriptionInvoice',
    }),
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.filter.startDate = null
      this.filter.endDate = null
      this.fetchPaymentHistories()
    },
    async fetchPaymentHistories() {
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        const filteredData = {
          ...this.filter,
          startDate: this.filter.startDate !== null ? toYearWithDate(this.filter.startDate) : '',
          endDate: this.filter.endDate !== null ? toYearWithDate(this.filter.endDate) : '',
        }
        const response = await this.getSubscriptionInvoice({
          search: filteredData,
          page: this.currentPage,
          limit: this.perPage,
          permission: adminPermissions.VIEW_SUBSCRIPTION_HISTORY,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.reportData = data.data
          this.totalCount = data.totalItems
          this.isBusy = false
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
.invoice-filter-select {
  min-width: 190px;
  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }
  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
